import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const STATUS_TABS = [
  { id: "pending_review", label: "Awaiting review" },
  { id: "draft", label: "Drafts" },
  { id: "published", label: "Published" },
];

const STATUS_PILL = {
  draft: "bg-[#F4EFE6] text-[#1E3A8A] border-[#1E3A8A]/30",
  pending_review: "bg-[#FFF8E1] text-[#A87900] border-[#F4C430]/60",
  published: "bg-[#E8F5E9] text-[#1B5E20] border-[#2E7D32]/40",
};

export default function AdminEvents() {
  const [tab, setTab] = useState("pending_review");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/events?status=${status}`);
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(tab);
  }, [tab]);

  const publish = async (id) => {
    try {
      await api.post(`/admin/events/${id}/publish`);
      toast.success("Event published");
      await load(tab);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Publish failed");
    }
  };

  const unpublish = async (id) => {
    if (!window.confirm("Hide this event from the public calendar?")) return;
    try {
      await api.post(`/admin/events/${id}/unpublish`);
      toast.success("Event unpublished");
      await load(tab);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unpublish failed");
    }
  };

  return (
    <div data-testid="admin-events" className="p-8 lg:p-12">
      <p className="label-eyebrow text-[#1E3A8A]">— Calendar</p>
      <h1 className="font-serif-display text-4xl font-medium tracking-tight text-[#111] mt-2">
        Event queue
      </h1>
      <p className="text-sm text-[#555] mt-2 max-w-2xl">
        Writers and artists submit events here. Publish to push them to the public events calendar.
      </p>

      <div className="mt-8 flex items-center gap-2 border-b border-[#E5E0D8]" data-testid="event-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`event-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`label-eyebrow px-4 py-3 border-b-2 ${
              tab === t.id ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#555] hover:text-[#111]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {loading ? (
          <p className="label-eyebrow text-[#555]">— Loading</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#555]">Nothing here.</p>
        ) : (
          items.map((ev) => (
            <div
              key={ev.id}
              data-testid={`admin-event-row-${ev.id}`}
              className="border border-[#E5E0D8] bg-white p-5 flex items-start justify-between gap-6"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`label-eyebrow px-2 py-0.5 border ${STATUS_PILL[ev.status] || STATUS_PILL.draft}`}>
                    {(ev.status || "draft").replace("_", " ").toUpperCase()}
                  </span>
                  <h3 className="font-serif-display text-xl tracking-tight text-[#111]">{ev.title}</h3>
                </div>
                <p className="text-xs text-[#555] mt-2">
                  By {ev.organizer_name} ({ev.organizer_role}) ·{" "}
                  {new Date(ev.start_at).toLocaleString()}
                  {ev.location ? ` · ${ev.location}` : ""}
                </p>
                <p className="text-sm text-[#333] mt-2 line-clamp-3">{ev.description}</p>
                {ev.external_url && (
                  <a
                    href={ev.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#1E3A8A] mt-2 inline-block hover:underline"
                  >
                    {ev.external_url}
                  </a>
                )}
              </div>
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                {(ev.status === "pending_review" || ev.status === "draft") && (
                  <button
                    onClick={() => publish(ev.id)}
                    data-testid={`publish-event-${ev.id}`}
                    className="label-eyebrow text-[#1B5E20] hover:text-[#111]"
                  >
                    Publish
                  </button>
                )}
                {ev.status === "published" && (
                  <button
                    onClick={() => unpublish(ev.id)}
                    data-testid={`unpublish-event-${ev.id}`}
                    className="label-eyebrow text-[#555] hover:text-[#D32F2F]"
                  >
                    Unpublish
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
